import _ from 'lodash';
import moment from 'moment-timezone';
import { useState } from 'react';
import { quartzCronExpressionHelper } from '@/annotation/reportContent.utilities';
import { getTimeRounded } from '@/datetime/dateTime.utilities';
import { DURATION_TIME_UNITS } from '@/main/app.constants';
import { DEFAULT_LIVE_RATE } from '@/schedule/schedule.constants';
import { ScheduleTypeName } from '@/schedule/schedule.types';
import { defaultScheduleType, determineTimeEntries } from '@/schedule/schedule.utilities';
import { ScheduleParser } from '@/schedule/scheduleParser';
import type { Timezone } from '@/datetime/timezone.service';
import type {
  AutoUpdateTimeScheduleEntry,
  LiveUpdateRate,
  ScheduleType,
  UpdatedSchedule,
} from '@/schedule/schedule.types';

export const useSchedule = (
  cronSchedule: string[] | undefined,
  updatedSchedule: UpdatedSchedule | undefined,
  initialScheduleType: ScheduleTypeName,
  timezoneDefault: Timezone | undefined,
  defaultLiveRate: LiveUpdateRate = DEFAULT_LIVE_RATE,
) => {
  /**
   * Determine whether the schedule has been configured or not.
   *
   * @returns - false if a cronSchedule is missing or set to the pending schedule true otherwise
   */
  function isFirstTimeConfiguring(): boolean {
    return _.isEmpty(cronSchedule);
  }

  const [timezone, setTimezone] = useState<Timezone | undefined>(updatedSchedule?.timezone ?? timezoneDefault);

  const [irregularSchedule, setIrregularSchedule] = useState<string[]>();

  const [selectedScheduleType, setSelectedScheduleType] = useState((): ScheduleType => {
    if (updatedSchedule?.selectedScheduleType) {
      return updatedSchedule.selectedScheduleType;
    }

    if (isFirstTimeConfiguring()) {
      return _.merge(defaultScheduleType(), { selectedType: initialScheduleType });
    }

    try {
      return ScheduleParser.determineScheduleType(cronSchedule ?? [], quartzCronExpressionHelper());
    } catch (e) {
      setIrregularSchedule(cronSchedule);
    }

    return defaultScheduleType();
  });

  const [timeEntries, setTimeEntries] = useState((): AutoUpdateTimeScheduleEntry[] => {
    if (updatedSchedule?.timeEntries) {
      return updatedSchedule.timeEntries;
    }

    // Default time entry is the current time rounded to the previous 15 minute increment. This is to prevent the
    // report from running shortly after being configured (CRAB-50435).
    const defaultTime = getTimeRounded(moment().subtract(15, 'minutes'), timezoneDefault);

    // "Live" date range does not have time entries
    if (isFirstTimeConfiguring() || selectedScheduleType.selectedType === ScheduleTypeName.LIVE) {
      return [{ time: defaultTime }];
    }

    try {
      const allTimeEntries: AutoUpdateTimeScheduleEntry[] = [];
      _.forEach(cronSchedule, (schedule) => {
        const { minutes, hours } = quartzCronExpressionHelper().parse(schedule);
        allTimeEntries.push(...determineTimeEntries(minutes, hours));
      });

      return allTimeEntries;
    } catch (e) {
      setIrregularSchedule(cronSchedule);
    }

    return [{ time: defaultTime }];
  });

  const [liveRate, setLiveRate] = useState((): LiveUpdateRate => {
    if (updatedSchedule?.liveRate) {
      return updatedSchedule.liveRate;
    }

    if (isFirstTimeConfiguring() || selectedScheduleType.selectedType !== ScheduleTypeName.LIVE) {
      return defaultLiveRate;
    }

    const helper = quartzCronExpressionHelper();
    const rate = cronSchedule?.map(helper.cronScheduleToRate).find((rate) => rate !== undefined);

    if (_.isNil(rate)) {
      setIrregularSchedule(cronSchedule);

      return defaultLiveRate;
    }

    return {
      value: rate.value,
      unit: DURATION_TIME_UNITS.find(({ unit }) => unit.includes(rate.units))!,
    };
  });

  const onLiveScheduleInvalidInput = () => {
    setLiveRate(defaultLiveRate);
    setIrregularSchedule(cronSchedule);
  };

  return {
    selectedScheduleType,
    setSelectedScheduleType,
    isLive: selectedScheduleType.selectedType === ScheduleTypeName.LIVE,
    liveRate,
    setLiveRate,
    timeEntries,
    setTimeEntries,
    irregularSchedule,
    setIrregularSchedule,
    onLiveScheduleInvalidInput,
    timezone,
    setTimezone,
  };
};
